import { take } from 'lodash';

import { type BannerTargeting, findMatchingBanners } from '@skippay/core/modules/banners/filters';
import type { Banner } from '@skippay/core/modules/banners/types';

import { bannerSlugsToExclude } from 'utils/banners';

import { LIST_CAPS } from '../../constants';
import { ActionFlag } from '../../masking';
import { AccountPackage, type CustomerContext, type OfferItem } from '../../types';
import type { DerivedSource } from '../types';

const toTargeting = (ctx: Readonly<CustomerContext>): BannerTargeting => ({
	hasMaxiPackage: ctx.account?.package ? ctx.account.package === AccountPackage.MAXI : undefined,
	canApplyForRevolving: ctx.eligibility?.canApplyForRevolving,
	slugsToExclude: bannerSlugsToExclude({
		hasActiveDeferredPayment: (ctx.deferredPayments?.length ?? 0) > 0,
		hasActivePayInThree: (ctx.payInThree?.length ?? 0) > 0,
		hasPreferredBank: ctx.account?.hasPreferredBank,
	}),
});

const dedupeByPromoName = (banners: Banner[]): Banner[] => {
	const seen = new Set<string>();
	return banners.filter((banner) => {
		if (seen.has(banner.promoName)) return false;
		seen.add(banner.promoName);
		return true;
	});
};

const toOffer = (banner: Banner): OfferItem => ({
	id: banner.promoName,
	label: banner.botLabel || '',
	description: banner.botDescription || '',
	url: banner.bannerUrl || '',
});

export const bannersToOffers = (banners: Banner[], ctx: Readonly<CustomerContext>): OfferItem[] => {
	if (ctx.compliance?.actionsRequired?.includes(ActionFlag.ACCOUNT_BLOCKED)) return [];

	return take(
		dedupeByPromoName(findMatchingBanners(banners, 'overview', toTargeting(ctx))),
		LIST_CAPS.offers
	).map(toOffer);
};

export const offersSource = (getBanners: () => Banner[]): DerivedSource => ({
	kind: 'derived',
	domains: ['offers'],
	toModel: (ctx) => (ctx.compliance ? { offers: bannersToOffers(getBanners(), ctx) } : {}),
});
